import {
  Box,
  Flex,
  Tag,
  TagLabel,
  TagCloseButton,
  Text,
  Link,
  BoxProps,
  Tooltip,
} from '@tapresearch/ui/components';

import React, { useCallback, useContext, useMemo } from 'react';

import { filterContext } from '../FilterProvider';

export interface FilterBadgesProps extends BoxProps {
  isDisabled?: boolean;
  onTagClicked?: () => void;
  omitFilters?: string[];
  filterNames?: Record<string, string>;
}

export function FilterBadges({
  omitFilters = [],
  filterNames = {},
  onTagClicked = () => {
    //
  },
  isDisabled = false,
  ...rest
}: FilterBadgesProps) {
  const { filterLabels, filterValues, onFilterItemChange, onClearFilters } =
    useContext(filterContext);

  const omitFiltersSet = useMemo(() => {
    return new Set(omitFilters);
  }, [omitFilters]);

  const [formattedFilterLabels, filterKeys] = useMemo(() => {
    const filterKeys: string[] = [];
    const innerFormattedFilterLabels = Object.keys(filterLabels)
      .filter((key) => !omitFiltersSet.has(key))
      .map((key) => {
        filterKeys.push(key);
        const label = filterLabels[key];
        const filterName = filterNames[key] || key;
        const valueOrLabel =
          (Array.isArray(label) && label.length > 0 && label) ||
          (!Array.isArray(label) && label) ||
          filterValues[key];

        const valueLabel = Array.isArray(valueOrLabel)
          ? valueOrLabel.join(',')
          : valueOrLabel;

        return [key, filterName, valueLabel];
      });
    return [innerFormattedFilterLabels, filterKeys];
  }, [filterLabels, filterValues, filterNames, omitFiltersSet]);

  const onTagRemoveClick = useCallback(
    (filterKey: string) => {
      onFilterItemChange(filterKey, undefined, undefined);
    },
    [onFilterItemChange]
  );

  const innerOnTackClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      onTagClicked();
    },
    [onTagClicked]
  );

  const innerOnClearFilters = useCallback(() => {
    onClearFilters(filterKeys);
  }, [filterKeys, onClearFilters]);

  if (
    formattedFilterLabels === undefined ||
    formattedFilterLabels.length === 0
  ) {
    return null;
  }

  return (
    <Box
      {...rest}
      data-testid={`filter-badges-wrapper${isDisabled ? '-disabled' : ''}`}
    >
      <Flex fontSize="sm" alignItems="center" mb="2.5">
        <Text data-testid="filter-badges-count" color="gray.500">
          Filter: {formattedFilterLabels.length}
        </Text>
        <Text ml="3">|</Text>
        <Link
          ml="5"
          fontSize="xs"
          fontWeight="bold"
          color="blue.600"
          _hover={{ color: 'blue.700', textDecoration: 'underline' }}
          data-testid="filter-badges-clear-button"
          onClick={(e) => {
            e.preventDefault();
            innerOnClearFilters();
          }}
        >
          Clear all
        </Link>
      </Flex>

      <Flex rowGap="8px" columnGap="8px" mb="2.5">
        {formattedFilterLabels.map(([filterKey, filterName, valueLabel]) => {
          return (
            <Tooltip
              key={`${filterName}-tag`}
              label={valueLabel}
              hasArrow
              placement="top"
            >
              <Tag
                {...TagStyle}
                colorScheme={isDisabled ? 'gray' : 'blue'}
                color={isDisabled ? 'gray.500' : 'blue.500'}
                bg={isDisabled ? 'gray.100' : 'blue.100'}
                data-testid={`filter-badge-${filterKey}`}
                onClick={innerOnTackClick}
              >
                <TagLabel {...TagLabelStyle}>
                  <Text
                    fontSize="sm"
                    mr="1"
                    as="span"
                    textTransform="capitalize"
                  >
                    {filterName}:
                  </Text>
                  <Text
                    textOverflow="ellipsis"
                    overflow="hidden"
                    fontSize="sm"
                    color={isDisabled ? 'gray.500' : 'black'}
                  >
                    {valueLabel}
                  </Text>
                </TagLabel>
                <TagCloseButton
                  data-testid={`filter-badges-remove-${filterKey}-filter-button`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onTagRemoveClick(filterKey as string);
                  }}
                />
              </Tag>
            </Tooltip>
          );
        })}
      </Flex>
    </Box>
  );
}

const TagStyle = {
  maxWidth: '250px',
  cursor: 'pointer',
  py: 1,
  px: 2,
  pr: 3,
  size: 'md',
  alignItems: 'center',
  colorScheme: 'blue',
  bg: 'blue.100',
  borderRadius: 'md',
  boxShadow: 'none',
  transitionProperty: 'common',
  transitionDuration: 'normal',
  color: 'blue.500',
  fontWeight: 'bold',
  _hover: {
    bg: 'blue.200',
    color: 'blue.600',
  },
};

const TagLabelStyle = {
  display: 'flex',
  flexGrow: 1,
  px: 2,
  alignItems: 'center',
};
